// --- Accounts Summary Page (Day-by-day) --- //

import React, { useEffect, useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase.js";
import Cookies from "js-cookie";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

export default function AccountsSummaryPage() {
    const [dailyCounts, setDailyCounts] = useState([]);
    const [labels, setLabels] = useState([]);
    const [totalAccounts, setTotalAccounts] = useState(0);
    const servicetablename = "accountingfinancedb";

    // ---------------------------
    // FETCH LEDGER ACCOUNTS + PROCESS LAST 7 DAYS
    // ---------------------------
    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const companyId = Cookies.get("select_company_uid");
                if (!companyId) return;

                const ref = collection(
                    db,
                    servicetablename,
                    companyId,
                    "ledger_accounts"
                );
                const snap = await getDocs(ref);

                const rows = snap.docs.map((d) => ({
                    id: d.id,
                    ...d.data(),
                    _date: d.data().date_time?.toDate
                        ? d.data().date_time.toDate()
                        : new Date(d.data().date_time),
                }));

                setTotalAccounts(rows.length);

                const today = new Date();
                const l = [];
                const counts = [];

                for (let i = 6; i >= 0; i--) {
                    const d = new Date();
                    d.setDate(today.getDate() - i);

                    l.push(
                        d.toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                        })
                    );

                    const count = rows.filter(
                        (v) => v._date.toDateString() === d.toDateString()
                    ).length;

                    counts.push(count);
                }

                setLabels(l);
                setDailyCounts(counts);
            } catch (err) {
                console.error("Error fetching accounts:", err);
            }
        };

        fetchAccounts();
    }, []);

    const primaryColor =
        getComputedStyle(document.documentElement)
            .getPropertyValue("--primary-color")
            .trim() || "#4f46e5";

    // ---------------------------
    // LINE CHART DATA
    // ---------------------------
    const chartData = useMemo(
        () => ({
            labels,
            datasets: [
                {
                    label: "Accounts",
                    data: dailyCounts,
                    borderColor: primaryColor,
                    backgroundColor: `${primaryColor}33`,
                    fill: true,
                    tension: 0.45,
                    borderWidth: 1,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    pointBackgroundColor: primaryColor,
                },
            ],
        }),
        [labels, dailyCounts, primaryColor]
    );

    // ---------------------------
    // LINE CHART OPTIONS
    // ---------------------------
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => `Accounts: ${ctx.parsed.y}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { font: { size: 10 } },
            },
            y: {
                beginAtZero: true,
                grid: { color: "rgba(0,0,0,0.06)" },
                ticks: { font: { size: 10 } },
            },
        },
    };

    return (
        <div className="p-11">
            <div className="b-bottom text-h2 p-1">
                Accounts – Last 7 Days — {totalAccounts}
            </div>

            <div style={{ height: "220px" }}>
                <Line data={chartData} options={chartOptions} />
            </div>
        </div>
    );
}
